import axios from 'axios';
import http from '../http-common';

const DIRECTUS_URL =
    process.env.REACT_APP_DIRECTUS_URL || 'https://docs.clockme.eu';
const DIRECTUS_TOKEN = process.env.REACT_APP_DIRECTUS_TOKEN || '';
const DIRECTUS_VERSIONS_COLLECTION =
    process.env.REACT_APP_DIRECTUS_VERSIONS_COLLECTION || 'legacy_Web_versions';
const DIRECTUS_CHANGES_COLLECTION =
    process.env.REACT_APP_DIRECTUS_CHANGES_COLLECTION || 'legacy_web_changes';

const directus = axios.create({
    baseURL: DIRECTUS_URL,
    withCredentials: false,
    headers: {
        'Content-type': 'application/json',
    },
});

// Do not send the app JWT to Directus (axios.create still merges axios.defaults).
directus.interceptors.request.use(config => {
    if (DIRECTUS_TOKEN) {
        config.headers.Authorization = `Bearer ${DIRECTUS_TOKEN}`;
    } else if (config.headers) {
        delete config.headers.Authorization;
        if (config.headers.common) {
            delete config.headers.common.Authorization;
        }
    }
    config.withCredentials = false;
    return config;
});

const getAll = () => {
    return http.get(`/v1/web-versions/`);
};

const getCurrentClient = () => {
    if (typeof window === 'undefined' || !window.location) return '';
    const host = (window.location.hostname || '').toLowerCase();
    const firstDot = host.indexOf('.');
    return firstDot === -1 ? host : host.slice(0, firstDot);
};

const isVisibleForCurrentHost = hostsField => {
    if (hostsField === undefined || hostsField === null || hostsField === '') {
        return true;
    }
    const current = getCurrentClient();
    const tokens = String(hostsField)
        .toLowerCase()
        .split(',')
        .map(s => s.trim())
        .filter(Boolean);
    if (tokens.length === 0 || tokens.indexOf('all') !== -1) return true;
    return tokens.indexOf(current) !== -1;
};

const buildChangelogFromDirectus = (versionsRes, changesRes) => {
    const versions = (versionsRes.data && versionsRes.data.data) || [];
    const changes = (changesRes.data && changesRes.data.data) || [];

    const byVersion = {};
    changes
        .filter(c => isVisibleForCurrentHost(c.hosts))
        .forEach(c => {
            const vid =
                c.version_id && typeof c.version_id === 'object'
                    ? c.version_id.version_id
                    : c.version_id;
            if (!byVersion[vid]) byVersion[vid] = {};
            if (!byVersion[vid][c.category]) byVersion[vid][c.category] = [];
            byVersion[vid][c.category].push({
                description: c.description,
                description_en: c.description_en,
                description_es: c.description_es,
                change_id: c.change_id,
            });
        });

    return versions
        .map(v => ({
            version_id: v.version_id,
            version_number: v.version_number,
            release_date: v.release_date,
            changes: byVersion[v.version_id] || {},
        }))
        .filter(v => Object.keys(v.changes).length > 0);
};

const getAllWithChangesFromDirectus = async () => {
    const [versionsRes, changesRes] = await Promise.all([
        directus.get(`/items/${DIRECTUS_VERSIONS_COLLECTION}`, {
            params: {
                fields: 'version_id,version_number,release_date',
                sort: '-version_number',
                limit: -1,
            },
        }),
        directus.get(`/items/${DIRECTUS_CHANGES_COLLECTION}`, {
            params: {
                fields:
                    'change_id,version_id,category,description,description_en,description_es,hosts',
                sort: 'change_id',
                limit: -1,
            },
        }),
    ]);

    return buildChangelogFromDirectus(versionsRes, changesRes);
};

const getAllWithChanges = async () => {
    try {
        const data = await getAllWithChangesFromDirectus();
        return { data };
    } catch (directusError) {
        console.warn(
            'Directus changelog unavailable, using API fallback',
            directusError
        );
        const res = await http.get('/v1/web-versions/withChanges');
        return { data: res.data || [] };
    }
};

const get = id => {
    return http.get(`/v1/web-versions/${id}`);
};

const create = data => {
    return http.post('/v1/web-versions', data);
};

const update = (id, data) => {
    return http.put(`/v1/web-versions/${id}`, data);
};

const remove = id => {
    return http.delete(`/v1/web-versions/${id}`);
};

const removeAll = () => {
    return http.delete(`/v1/web-versions`);
};

export default {
    getAll,
    getAllWithChanges,
    get,
    create,
    update,
    remove,
    removeAll,
};
